import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMagicLinkVerification from '../../../access/useMagicLinkVerification';
import {
  createFormFlowApi,
  requestFlowMagicLink,
  verifyFlowMagicLink,
} from './formFlowApiClient';
import useFormAnswers from '../useFormAnswers';
import { hasPendingUploads, stripClientOnlyFields } from '../answerPath';
import { createFlowStepAdapter } from '../adapters/flowStepAdapter';

const SSE_FALLBACK_MS = 3000;
const EMPTY_VALIDATION = Object.freeze({ errors: {}, warnings: {} });
const PENDING_UPLOAD_MESSAGE = 'Please wait for uploads to finish before submitting. Remove any failed uploads or retry them.';

/**
 * Map a 401/403 error body from {@code FormFlowController} to a gate-state string that the
 * shell consumes. The body is produced by {@code FlowAuthRequiredException} or
 * {@code FlowAccessDeniedException}, which serialise {@code accessMode}, {@code componentId}
 * and {@code formSessionId}.
 */
function deriveGateState(status, accessMode) {
  if (status === 403) {
    if (accessMode === 'DISABLED') return 'disabled';
    return 'access_denied';
  }
  if (status === 401) {
    if (accessMode === 'EMAIL_MAGIC_LINK') return 'email_required';
    if (typeof accessMode === 'string' && accessMode.startsWith('AUTHENTICATED')) {
      return 'auth_required';
    }
  }
  return null;
}

/**
 * Normalise a server validation payload into the shape {@code DynamicFormRenderer} consumes.
 */
function normaliseValidation(raw) {
  if (!raw || typeof raw !== 'object') return EMPTY_VALIDATION;
  if (raw.errors && typeof raw.errors === 'object' && !Array.isArray(raw.errors)) {
    return {
      errors: raw.errors || {},
      warnings: raw.warnings || {},
    };
  }
  if (Array.isArray(raw.fieldErrors)) {
    const errors = {};
    const warnings = {};
    for (const item of raw.fieldErrors) {
      if (!item || typeof item !== 'object') continue;
      const key = item.fieldKey || item.key;
      if (!key) continue;
      const bucket = item.level === 'warning' ? warnings : errors;
      bucket[key] = item.message || 'Invalid';
    }
    return { errors, warnings };
  }
  if (Array.isArray(raw)) {
    const errors = {};
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const key = item.fieldKey || item.key;
      if (!key) continue;
      errors[key] = item.message || 'Invalid';
    }
    return { errors, warnings: {} };
  }
  return { errors: raw, warnings: {} };
}

/**
 * Client-side state machine for the respondent-facing Form Flow shell.
 *
 * Composes {@link useFormAnswers} via {@link createFlowStepAdapter} for every answer-side
 * behaviour (autosave, localStorage backup, upload/remove/retry, Save draft button,
 * _file stripping). Keeps flow-specific concerns inline: session load, stepper, SSE
 * transitions, back navigation, submit outcomes, magic-link gate.
 */
export default function useFormFlow({ formSessionId, test = false }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // idle | submitting | transitioning | completed | error
  const [submitState, setSubmitState] = useState('idle');
  const [completion, setCompletion] = useState(null);
  const [sseError, setSseError] = useState(false);
  const [validation, setValidation] = useState(EMPTY_VALIDATION);

  const [gateState, setGateState] = useState(null);
  const [gateComponentId, setGateComponentId] = useState(null);

  const {
    grantToken,
    verificationError,
    clearGrantToken,
  } = useMagicLinkVerification(gateComponentId || '', verifyFlowMagicLink);

  const api = useMemo(
    () => createFormFlowApi(formSessionId, { test, grantToken: grantToken || null }),
    [formSessionId, test, grantToken],
  );

  // Tracks the session's current step so the adapter can read it at each call without
  // requiring adapter recreation on step transitions.
  const currentComponentIdRef = useRef(null);
  useEffect(() => {
    currentComponentIdRef.current = session?.currentStep?.componentId || null;
  }, [session]);

  const adapter = useMemo(
    () => createFlowStepAdapter(api, () => currentComponentIdRef.current),
    [api],
  );

  const submitting = submitState === 'submitting';

  const core = useFormAnswers({
    adapter,
    resourceId: formSessionId,
    grantToken: grantToken || null,
    submitting,
  });
  const {
    answers,
    answersRef,
    autoSaveStatus,
    lastSavedAt,
    savingDraft,
    onChangeAnswer: coreOnChangeAnswer,
    onUpload,
    onRemoveFile,
    retryUpload,
    saveDraft,
    requestServerAutoSave,
    cancelPendingServerAutoSave,
    hydrate,
    restoreFromLocalDraft,
    setLastSavedAtFromServer,
    clearLocalBackup,
  } = core;

  const fallbackTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await api.getSession();
      setSession(s);
      setGateState(null);
      setGateComponentId(null);

      const saved = s?.currentStep?.savedDraft?.answers;
      const savedAt = s?.currentStep?.savedDraft?.updatedAt;
      const serverHasAnswers = saved && Object.keys(saved).length > 0;
      if (serverHasAnswers) {
        hydrate({ ...saved });
        if (savedAt) setLastSavedAtFromServer(savedAt);
      } else {
        const restored = restoreFromLocalDraft();
        if (!restored) hydrate({});
      }

      setValidation(EMPTY_VALIDATION);
      if (s?.status === 'SUBMITTED') {
        setSubmitState('completed');
      } else if (s?.status === 'TRANSITIONING') {
        setSubmitState('transitioning');
      } else {
        setSubmitState('idle');
      }
      setSseError(false);
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        setError(null);
        setSession(null);
      } else {
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [api, hydrate, restoreFromLocalDraft, setLastSavedAtFromServer]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (verificationError) {
      setGateState('email_required');
      setError(null);
    }
  }, [verificationError]);

  // Wrap core.onChangeAnswer so a keystroke:
  //   1. updates answers + writes localStorage (core)
  //   2. schedules a debounced server save (core)
  //   3. clears the per-field server-validation error optimistically (flow-specific)
  //
  // Form flow has no onBlur-driven save (the renderer's onFieldBlur is a no-op for
  // flow), so the per-keystroke requestServerAutoSave call is how flow gets its
  // pre-refactor "save to server on every change, debounced" behaviour.
  const onChangeAnswer = useCallback((key, value) => {
    coreOnChangeAnswer(key, value);
    requestServerAutoSave();
    setValidation((prev) => {
      if (!prev?.errors || !(key in prev.errors)) return prev;
      const nextErrors = { ...prev.errors };
      delete nextErrors[key];
      return { errors: nextErrors, warnings: prev.warnings || {} };
    });
  }, [coreOnChangeAnswer, requestServerAutoSave]);

  const onFieldBlur = useCallback(() => {
    // Reserved for future server-side per-field validation; no-op today but threaded
    // through so the renderer's onBlur callback site has a safe target. Server autosave
    // is already covered by onChangeAnswer.
  }, []);

  const scheduleFallback = useCallback(() => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      load();
    }, SSE_FALLBACK_MS);
  }, [load]);

  const clearFallback = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearFallback(), [clearFallback]);

  const submit = useCallback(async () => {
    const componentId = currentComponentIdRef.current;
    if (!componentId) return;
    if (hasPendingUploads(answersRef.current)) {
      setError(new Error(PENDING_UPLOAD_MESSAGE));
      setSubmitState('idle');
      return;
    }
    // Kill pending autosave — see cancelPendingServerAutoSave comment. Without this, a
    // timer scheduled by a last keystroke can fire post-transition and tag the PREVIOUS
    // step's answers against the NEW step's componentId.
    cancelPendingServerAutoSave();
    setSubmitState('submitting');
    setError(null);
    setValidation(EMPTY_VALIDATION);
    try {
      const result = await adapter.submit(
        componentId,
        stripClientOnlyFields(answersRef.current),
      );
      if (!result || !result.outcome) {
        throw new Error('Unexpected submit response — no outcome.');
      }
      clearLocalBackup();

      if (result.outcome === 'CROSS_FLOW_HANDOFF') {
        if (!result.handoffUrl) {
          throw new Error('Server returned CROSS_FLOW_HANDOFF without a handoffUrl.');
        }
        window.location.assign(result.handoffUrl);
        return;
      }
      if (result.outcome === 'COMPLETION') {
        if (result.completionPayload) {
          setCompletion(result.completionPayload);
        } else {
          setCompletion((prev) => prev || { message: 'Thanks!' });
        }
        setSubmitState('completed');
        await load();
        return;
      }
      if (result.outcome === 'TRANSITIONING') {
        setSubmitState('transitioning');
        setSseError(false);
        scheduleFallback();
        return;
      }
      // NEXT_STEP | BACK_STEP | CROSS_FLOW_NEXT_STEP → refetch
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      if (status === 400 && (body.errors || body.fieldErrors)) {
        setValidation(normaliseValidation(body));
        setSubmitState('idle');
        return;
      }
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        if (nextGate !== 'email_required') clearGrantToken();
        setSubmitState('idle');
        return;
      }
      setError(e);
      setSubmitState('error');
    }
  }, [adapter, answersRef, clearLocalBackup, load, scheduleFallback, clearGrantToken, cancelPendingServerAutoSave]);

  const back = useCallback(async (targetComponentId) => {
    cancelPendingServerAutoSave();
    setSubmitState('submitting');
    setError(null);
    try {
      await adapter.back(targetComponentId);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        if (nextGate !== 'email_required') clearGrantToken();
        setSubmitState('idle');
        return;
      }
      setError(e);
      setSubmitState('error');
    }
  }, [adapter, load, clearGrantToken, cancelPendingServerAutoSave]);

  const refresh = useCallback(() => load(), [load]);
  const hasPendingUploadAnswers = useMemo(() => hasPendingUploads(answers), [answers]);

  const onStreamEvent = useCallback((eventName /* parsed */) => {
    if (eventName === 'step_ready') {
      clearFallback();
      setSseError(false);
      load();
      return;
    }
    if (eventName === 'error') {
      setSseError(true);
      return;
    }
  }, [clearFallback, load]);

  const retryTransitioning = useCallback(() => {
    setSseError(false);
    scheduleFallback();
    load();
  }, [scheduleFallback, load]);

  const requestMagicLink = useCallback(async (email) => {
    if (!gateComponentId) {
      throw new Error('No step componentId available to scope the magic link to.');
    }
    await requestFlowMagicLink(gateComponentId, email);
  }, [gateComponentId]);

  const onSignedIn = useCallback(() => {
    setGateState(null);
    load();
  }, [load]);

  return {
    session,
    answers,
    loading,
    error,
    submitState,
    hasPendingUploadAnswers,
    completion,
    sseError,
    validation,
    gateState,
    gateComponentId,
    streamUrl: adapter.streamUrl(),
    onStreamEvent,
    retryTransitioning,
    autoSaveStatus,
    lastSavedAt,
    savingDraft,
    saveDraft,
    onChangeAnswer,
    onFieldBlur,
    onUpload,
    onRemoveFile,
    retryUpload,
    submit,
    back,
    refresh,
    requestMagicLink,
    onSignedIn,
  };
}
